import { useEffect, useLayoutEffect, useState } from 'react'
import type { NoteAsset } from '../assets/assetTypes'

/**
 * One screenshot as Unpack shows it, at full resolution: the image on this device, else the full image downloaded once on demand (a
 * device that synchronised the note but not its images, such as a desktop showing a run made on the phone). The signed link is the
 * thumbnail, so it only stands in while the download runs or when it fails: a crop zoomed into it was blurred.
 */
export type AssetImageState = 'ready' | 'loading' | 'missing'

/** Downloads in flight or done this session, so a card's photo, its whole screenshot and the review share one download. */
const downloads = new Map<string, Promise<Blob>>()

export function useAssetImage(asset: NoteAsset | null | undefined, remoteUrl: string | undefined, loadFullImage?: (asset: NoteAsset) => Promise<Blob>): { source: string | null; state: AssetImageState; full: boolean } {
  const local = asset?.processedMasterBlob ?? asset?.sourceBlob ?? null
  const [downloaded, setDownloaded] = useState<Blob | null>(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    if (!asset || local || !loadFullImage) return
    let live = true
    let pending = downloads.get(asset.id)
    if (!pending) {
      pending = loadFullImage(asset)
      downloads.set(asset.id, pending)
      pending.catch(() => downloads.delete(asset.id))
    }
    pending.then(blob => { if (live) setDownloaded(blob) }, () => { if (live) setFailed(true) })
    return () => { live = false }
  }, [asset, local, loadFullImage])
  const blob = local ?? downloaded
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  useLayoutEffect(() => {
    if (!blob) { setBlobUrl(null); return }
    const url = URL.createObjectURL(blob)
    setBlobUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [blob])
  const source = blobUrl ?? remoteUrl ?? null
  return { source, state: source ? 'ready' : !asset || failed || !loadFullImage ? 'missing' : 'loading', full: Boolean(blobUrl) }
}
